import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateRange from 'components/Form/DateRange';
import Telephone from 'components/Form/Telephone';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { SubstanceAlcoholVoluntaryCounselingItemModel } from 'forms/sections/substance/alcohol/counseling/voluntary/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import NotApplicable from 'components/Form/NotApplicable';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import TelephoneCollection from 'components/Form/Telephone/TelephoneCollection';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';
import PVQName from 'components/Form/PVQName';
import { counselingRecommendedByOptions } from 'constants/enums/substanceOptions';
import Checkbox from 'components/Form/Checkbox';
import {
  ApplicableModel, CountryArrayModel, CountryModel, LocationModel
} from 'forms/shared/types';
import { isSafeInternational, isSafeUnitedStates } from 'helpers/locations';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';

interface Props
  extends ValidationElementProps,
  Partial<SubstanceAlcoholVoluntaryCounselingItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  formType?: string
}

export default class VoluntaryCounseling extends ValidationElement<Props> {
  update = (values: Partial<SubstanceAlcoholVoluntaryCounselingItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        CounselingDates: this.props.CounselingDates,
        TreatmentProviderName: this.props.TreatmentProviderName,
        TreatmentProviderAddress: this.props.TreatmentProviderAddress,
        TreatmentProviderTelephone: this.props.TreatmentProviderTelephone,
        CompletedTreatment: this.props.CompletedTreatment,
        NoCompletedTreatmentExplanation: this.props.NoCompletedTreatmentExplanation,
        Comments: this.props.Comments,
        CompletedTreatmentComments: this.props.CompletedTreatmentComments,
        RecommendedBy: this.props.RecommendedBy,
        RecommendedByExplanation: this.props.RecommendedByExplanation,
        TreatmentProviderNameNotApplicable: this.props.TreatmentProviderNameNotApplicable,
        PracticeName: this.props.PracticeName,
        PracticeNameNotApplicable: this.props.PracticeNameNotApplicable,
        TreatmentProviderPersonName: this.props.TreatmentProviderPersonName,
        TreatmentProviderAddressNotApplicable: this.props.TreatmentProviderAddressNotApplicable,
        TreatmentProviderPhones: this.props.TreatmentProviderPhones,
        TreatmentProviderEmails: this.props.TreatmentProviderEmails,
        ProviderStillPractices: this.props.ProviderStillPractices,
        ...values,
      });
    }
  };

  updateCounselingDates = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['CounselingDates']
  ) => {
    this.update({ CounselingDates: values });
  };

  updateTreatmentProviderName = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['TreatmentProviderName']
  ) => {
    this.update({ TreatmentProviderName: values });
  };

  updateTreatmentProviderAddress = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['TreatmentProviderAddress']
  ) => {
    this.update({ TreatmentProviderAddress: values });
  };

  updateTreatmentProviderTelephone = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['TreatmentProviderTelephone']
  ) => {
    this.update({ TreatmentProviderTelephone: values });
  };

  updateCompletedTreatment = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['CompletedTreatment']
  ) => {
    this.update({ CompletedTreatment: values });
  };

  updateNoCompletedTreatmentExplanation = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['NoCompletedTreatmentExplanation']
  ) => {
    this.update({ NoCompletedTreatmentExplanation: values });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateCompletedTreatmentComments = (values) => {
    this.update({
      CompletedTreatmentComments: values,
    });
  };

  /**
       * This is the list of people that recommended counseling or treatment
       */
  getPeopleWhoRecommendedCounseling = (cb) => {
    const selected = cb.value;
    let list = [...(this.props.RecommendedBy?.values || [])];

    if (list.includes(selected)) {
      list.splice(list.indexOf(selected), 1);
    } else if (selected === 'No' || list.includes('No')) {
      list = [selected];
    } else {
      list.push(selected);
    }
    return list;
  };

  updateRecommendedBy = (cb: SubstanceAlcoholVoluntaryCounselingItemModel['RecommendedBy']) => {
    const newList = this.getPeopleWhoRecommendedCounseling(cb);

    if (newList.includes('Other')) {
      this.update({
        RecommendedBy: {
          values: newList,
        },
      });
    } else {
      this.update({
        RecommendedBy: {
          values: newList,
        },
        RecommendedByExplanation: {},
      });
    }
  };

  updateRecommendedByExplanation = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['RecommendedByExplanation']
  ) => {
    this.update({ RecommendedByExplanation: values });
  };

  updateTreatmentProviderNameNotApplicable = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['TreatmentProviderNameNotApplicable']
  ) => {
    const updatedName = values?.applicable === false ? {} : this.props.TreatmentProviderName;
    const updatedPersonName = values?.applicable === false ? {} : this.props.TreatmentProviderName;
    this.update({
      TreatmentProviderNameNotApplicable: values,
      TreatmentProviderName: updatedName,
      TreatmentProviderPersonName: updatedPersonName,
    });
  };

  updatePracticeName = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['PracticeName']
  ) => {
    this.update({ PracticeName: values });
  };

  updatePracticeNameNotApplicable = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['PracticeNameNotApplicable']
  ) => {
    const updatedPracticeName = values?.applicable === false ? {} : this.props.PracticeName;
    this.update({
      PracticeNameNotApplicable: values,
      PracticeName: updatedPracticeName,
    });
  };

  updateTreatmentProviderPersonName = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['TreatmentProviderPersonName']
  ) => {
    this.update({ TreatmentProviderPersonName: values });
  };

  updateTreatmentProviderAddressNotApplicable = (
    treatmentProviderAddressNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<SubstanceAlcoholVoluntaryCounselingItemModel> = {
      TreatmentProviderAddressNotApplicable: { applicable: treatmentProviderAddressNotApplicable.applicable }
    };

    if (treatmentProviderAddressNotApplicable.applicable === false) {
      const treatmentProviderAddress: LocationModel | undefined = this.props.TreatmentProviderAddress;

      let country: CountryModel | string | CountryArrayModel | undefined = treatmentProviderAddress?.country;

      if (country) {
        if (isSafeInternational(treatmentProviderAddress)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.TreatmentProviderAddress = { country } as LocationModel;
      } else {
        queue.TreatmentProviderAddress = { } as LocationModel;
      }
    }

    this.update(queue);
  };

  updateTreatmentProviderPhones = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['TreatmentProviderPhones']
  ) => {
    this.update({
      TreatmentProviderPhones: values,
    });
  };

  updateTreatmentProviderEmails = (
    values: SubstanceAlcoholVoluntaryCounselingItemModel['TreatmentProviderEmails']
  ) => {
    this.update({
      TreatmentProviderEmails: values,
    });
  };

  updateProviderStillPractices = (values: SubstanceAlcoholVoluntaryCounselingItemModel['ProviderStillPractices']) => {
    this.update({
      ProviderStillPractices: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <>
        <Field
          title={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.recommendedBy')}
          attribute={`${attribute}.RecommendedBy`}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
        >
          <CheckboxGroup
            className="recommendedby option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.RecommendedBy || {}).values}
            attribute={`${attribute}.RecommendedBy`}
          >
            {counselingRecommendedByOptions.map((option) => (
              <Checkbox
                name="RecommendedBy"
                key={option}
                label={option}
                value={option}
                className="legal-charge-court-dismissed-checkbox"
                onUpdate={this.updateRecommendedBy}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>
        </Field>

        {((this.props.RecommendedBy || {}).values || []).includes('Other') && (
        <Field
          title={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.recommendedByExplanation')}
          titleSize="label"
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RecommendedByExplanation`}
        >
          <Textarea
            {...this.props.RecommendedByExplanation}
            name="RecommendedByExplanation"
            maxlength="100"
            className="recommended-by-explanation"

            onUpdate={this.updateRecommendedByExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.RecommendedByExplanation`}
          />
        </Field>
        )}

        <Field
          title={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.counselingDates')}
          attribute={`${attribute}.CounselingDates`}
          help="substance.pvq.alcohol.voluntaryCounseling.help.counselingDates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            {...this.props.CounselingDates}
            name="CounselingDates"
            attribute={`${attribute}.CounselingDates`}
            className="counseling-dates"
            minDateEqualTo
            onUpdate={this.updateCounselingDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.treatmentProviderName')}
          attribute={`${attribute}.TreatmentProviderName`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            {...this.props.TreatmentProviderNameNotApplicable}
            name="TreatmentProviderNameNotApplicable"
            className="substance-alcoholo-counseling-treatmentprovidernamenotapplicable"
            onUpdate={this.updateTreatmentProviderNameNotApplicable}
            onError={this.props.onError}
            or={i18n.m('substance.pvq.alcohol.voluntaryCounseling.para.or')}
            label={i18n.t('substance.pvq.alcohol.voluntaryCounseling.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProviderNameNotApplicable`}
            includeNAConfirmation={Boolean(this.props.TreatmentProviderName?.value)}
          >
            <PVQName
              {...this.props.TreatmentProviderPersonName}
              attribute={`${attribute}.TreatmentProviderPersonName`}
              exactAttribute
              aria-label="Provide treatment provider person name"
              name="TreatmentProviderPersonName"
              onUpdate={this.updateTreatmentProviderPersonName}
              onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showLastFirstMiddleNameOrder
              hideMiddleName
            />
          </NotApplicable>
        </Field>

        <Branch
          {...this.props.ProviderStillPractices}
          name="ProviderStillPractices"
          attribute={`${attribute}.ProviderStillPractices`}
          label={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.providerStillPractices')}
          labelSize="h4"
          className="providerstillpractices"

          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateProviderStillPractices}
          scrollIntoView={this.props.scrollIntoView}
          showDNK
        />

        {this.props.ProviderStillPractices?.value === 'Yes' && (
        <>
          <Field
            title={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.treatmentProviderPracticeName')}
            attribute={`${attribute}.PracticeName`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <NotApplicable
              {...this.props.PracticeNameNotApplicable}
              name="PracticeNameNameNotApplicable"
              className="substance-alcoholo-counseling-practicenamenotapplicable"
              onUpdate={this.updatePracticeNameNotApplicable}
              onError={this.props.onError}
              or={i18n.m('substance.pvq.alcohol.voluntaryCounseling.para.or')}
              label={i18n.t('substance.pvq.alcohol.voluntaryCounseling.label.idk')}
              required={this.props.required}
              attribute={`${attribute}.PracticeNameNotApplicable`}
              includeNAConfirmation={Boolean(this.props.PracticeName?.value)}
            >
              <Text
                {...this.props.PracticeName}
                name={`${attribute}.PracticeName`}
                attribute="PracticeName"
                className="treatment-provider-name"
                onUpdate={this.updatePracticeName}
                onError={this.props.onError}
                required={this.props.required}
              />
            </NotApplicable>
          </Field>

          <TelephoneCollection
            {...this.props.TreatmentProviderPhones}
            onUpdate={this.updateTreatmentProviderPhones}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProviderPhones`}
            telephoneFieldTitle={i18n.m('substance.pvq.alcohol.voluntaryCounseling.heading.treatmentProviderTelephone')}
            showTimeOfDay
            allowNotApplicable
            hideInitialBranchQuestion
          />

          <EmailCollection
            {...this.props.TreatmentProviderEmails}
            onUpdate={this.updateTreatmentProviderEmails}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProviderEmails`}
            emailFieldTitle={i18n.m('substance.pvq.alcohol.voluntaryCounseling.heading.treatmentProviderEmail')}
            showEmailUnknown
            includeEmailType={false}
            hasSecondaryAppendLabels
            secondaryNoAppendLabel="No"
            secondaryYesAppendLabel="Add"
          />

          <Field
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.TreatmentProviderAddress`}
            adjustFor="address"
            exactAttribute
          >
            <PVQLocationUnknown
              {...this.props.TreatmentProviderAddress}
              addressUnknown={this.props.TreatmentProviderAddressNotApplicable}
              addressUnknownLabel={i18n.t('substance.pvq.alcohol.voluntaryCounseling.label.idk')}
              attribute={`${attribute}.TreatmentProviderAddress`}
              name="TreatmentProvider-address"
              onUpdateAddressUnknown={this.updateTreatmentProviderAddressNotApplicable}
              showAddressUnknown
              addressBooks={this.props.addressBooks}
              addressBook="TreatmentProvider"
              onUpdate={this.updateTreatmentProviderAddress} // eslint-disable-line @typescript-eslint/unbound-method
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              dispatch={this.props.dispatch}
              required={this.props.required}
              geocode
              showApoFpoDpoPostcodeWhenForeign
              showInternationalStreet
              showMilitaryInstallation
              internationalStreetLabel={i18n.t('address.pvq.physicalAddress')}
              label={i18n.t(`substance.pvq.alcohol.voluntaryCounseling.heading.address`)}
              domesticTitle={i18n.t(`substance.pvq.alcohol.voluntaryCounseling.heading.addressDomesticTitle`)}
              foreignTitle={i18n.t(`substance.pvq.alcohol.voluntaryCounseling.heading.addressForeignTitle`)}
              hideInstallationNameUnknownWhenForeign
              exactAttribute
            />
          </Field>
        </>
        )}

        <Branch
          {...this.props.CompletedTreatment}
          name="CompletedTreatment"
          attribute={`${attribute}.CompletedTreatment`}
          label={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.completedTreatment')}
          labelSize="h4"
          className="completed-treatment"
          onUpdate={this.updateCompletedTreatment}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.CompletedTreatment?.value === 'No'
          && (
          <Field
            title={i18n.t('substance.pvq.alcohol.voluntaryCounseling.heading.noCompletedTreatment')}
            attribute={`${attribute}.NoCompletedTreatmentExplanation`}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.NoCompletedTreatmentExplanation}
              name="NoCompletedTreatmentExplanation"
              attribute={`${attribute}.NoCompletedTreatmentExplanation`}
              className="no-completed-treatment"
              onUpdate={this.updateNoCompletedTreatmentExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          )}
      </>
    ) : (
      <div className="voluntary-counseling">
        <Field
          title={i18n.t('substance.alcohol.voluntaryCounseling.heading.counselingDates')}
          attribute={`${attribute}.CounselingDates`}
          help="substance.alcohol.voluntaryCounseling.help.counselingDates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="CounselingDates"
            attribute={`${attribute}.CounselingDates`}
            className="counseling-dates"
            minDateEqualTo
            {...this.props.CounselingDates}
            onUpdate={this.updateCounselingDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>
        <Field
          title={i18n.t('substance.alcohol.voluntaryCounseling.heading.treatmentProviderName')}
          attribute={`${attribute}.TreatmentProviderName`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="TreatmentProviderName"
            attribute={`${attribute}.TreatmentProviderName`}
            className="treatment-provider-name"
            {...this.props.TreatmentProviderName}
            onUpdate={this.updateTreatmentProviderName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.alcohol.voluntaryCounseling.heading.treatmentProviderAddress')}
          attribute={`${attribute}.TreatmentProviderAddress`}
          optional
          adjustFor="address"
          help="substance.alcohol.voluntaryCounseling.help.treatmentProviderAddress"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="TreatmentProviderAddress"
            attribute={`${attribute}.TreatmentProviderAddress`}
            className="provider-address"
            {...this.props.TreatmentProviderAddress}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Provider"
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateTreatmentProviderAddress}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.alcohol.voluntaryCounseling.heading.treatmentProviderTelephone')}
          attribute={`${attribute}.TreatmentProviderTelephone`}
          help="substance.alcohol.voluntaryCounseling.help.treatmentProviderTelephone"
          className="override-required"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Telephone
            name="TreatmentProviderTelephone"
            attribute={`${attribute}.TreatmentProviderTelephone`}
            className="provider-telephone"
            {...this.props.TreatmentProviderTelephone}
            onUpdate={this.updateTreatmentProviderTelephone}
            onError={this.props.onError}
            allowNotApplicable={false}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Branch
          name="CompletedTreatment"
          attribute={`${attribute}.CompletedTreatment`}
          label={i18n.t('substance.alcohol.voluntaryCounseling.heading.completedTreatment')}
          labelSize="h4"
          className="completed-treatment"
          {...this.props.CompletedTreatment}
          onUpdate={this.updateCompletedTreatment}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Show when={this.props.CompletedTreatment?.value === 'No'}>
          <Field
            title={i18n.t('substance.alcohol.voluntaryCounseling.heading.noCompletedTreatment')}
            attribute={`${attribute}.NoCompletedTreatmentExplanation`}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="NoCompletedTreatmentExplanation"
              attribute={`${attribute}.NoCompletedTreatmentExplanation`}
              className="no-completed-treatment"
              {...this.props.NoCompletedTreatmentExplanation}
              onUpdate={this.updateNoCompletedTreatmentExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          <Comments
            title={i18n.t('substance.comments.optional')}
            name="CompletedTreatmentComments"
            className="optional-comment"
            attribute="CompletedTreatmentComments"
            {...this.props.CompletedTreatmentComments}
            onUpdate={this.updateCompletedTreatmentComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

VoluntaryCounseling.defaultProps = {
  CompletedTreatment: {},
  Comments: {},
  CompletedTreatmentComments: {},
  addressBooks: {},
  TreatmentProviderNameNotApplicable: { applicable: true }, // PVQ Only
  TreatmentProviderPersonName: {}, // PVQ Only
  TreatmentProviderAddressNotApplicable: { applicable: true }, // PVQ Only
  TreatmentProviderPhones: {}, // PVQ Only
  TreatmentProviderEmails: {}, // PVQ Only
  ProviderStillPractices: {}, // PVQ Only
  RecommendedBy: {}, // PVQ Only
  RecommendedByExplanation: {}, // PVQ Only
  PracticeName: {}, // PVQ Only
  PracticeNameNotApplicable: { applicable: true }, // PVQ Only
  dispatch: (action) => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
